import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./DateTimePicker.module.css";
import { fr, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";

const localeMap = {
  fr: fr,
  en: enUS,
};

const DateTimePicker = ({
  label,
  value,
  onChange,
  placeholder,
  minDate,
  maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
  disabled = false,
  required = false,
  readOnly = false,
  name,
  includeTime = true,
  showAgeBox = false,
}) => {
  const { i18n } = useTranslation();
  const dateFnsLocale = localeMap[i18n.language] || enUS;
  const currentYear = new Date().getFullYear();

  // 🔐 Sécurise la transformation de value
  const parsedValue = useMemo(() => {
    if (value instanceof Date) return value;
    if (typeof value === "string") {
      const d = new Date(value);
      return isNaN(d) ? null : d;
    }
    return null;
  }, [value]);

  const ageValue = useMemo(() => {
    if (!(parsedValue instanceof Date) || isNaN(parsedValue)) return "";
    const age = currentYear - parsedValue.getFullYear();
    return age >= 0 && age <= 50 ? age : "";
  }, [parsedValue, currentYear]);

  const handleAgeChange = (e) => {
    const input = e.target.value.trim();
    if (input === "") return;

    const age = parseInt(input, 10);
    if (!isNaN(age) && age >= 0 && age <= 50) {
      const today = new Date();
      const newDate = new Date(
        today.getFullYear() - age,
        today.getMonth(),
        today.getDate(),
        includeTime ? today.getHours() : 0,
        includeTime ? today.getMinutes() : 0
      );
      onChange(newDate);
    }
  };

  const actualPlaceholder =
    placeholder ?? (includeTime ? "aaaa-mm-jj hh:mm" : "aaaa-mm-jj");
  const dateFormat = includeTime ? "yyyy-MM-dd hh:mm aa" : "yyyy-MM-dd";

  return (
    <div className={styles.datePickerWrapper}>
      {label && (
        <label htmlFor={name} className={styles.quickFormLabel}>
          {label}
        </label>
      )}
      <div className={styles.inputGroup}>
        <DatePicker
          id={name}
          name={name}
          selected={parsedValue}
          onChange={readOnly ? undefined : onChange}
          showTimeSelect={includeTime}
          timeCaption="Heure"
          timeFormat="hh:mm aa"
          timeIntervals={15}
          dateFormat={dateFormat}
          placeholderText={actualPlaceholder}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          required={required}
          className={`${styles.quickFormInput} ${
            readOnly ? styles.readOnlyInput : ""
          }`}
          wrapperClassName={styles.wrapper}
          popperPlacement="bottom-start"
          locale={dateFnsLocale}
          readOnly={readOnly}
          showYearDropdown
          scrollableYearDropdown
          showMonthDropdown
          yearDropdownItemNumber={40}
        />

        {showAgeBox && (
          <input
            type="number"
            min={0}
            max={50}
            className={styles.ageInput}
            value={ageValue}
            onChange={handleAgeChange}
            placeholder="âge"
            disabled={readOnly || disabled}
          />
        )}
      </div>
    </div>
  );
};

export default DateTimePicker;
